import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import { portalApi, portalError } from "./portalApi";
import { ClipboardCheck, FileSignature, CheckCircle2, Clock, AlertTriangle, Sparkles, BellRing, X } from "lucide-react";
import { toast } from "sonner";

export default function PortalOnboarding() {
  const [d, setD] = useState(null);
  const load = () => portalApi.get("/portal/onboarding").then((r) => setD(r.data)).catch((e) => toast.error(portalError(e)));
  useEffect(() => { load(); }, []);
  const dismiss = async (nid) => {
    try {
      await portalApi.post(`/portal/notifications/${nid}/seen`);
      load();
    } catch (e) { toast.error(portalError(e)); }
  };
  if (!d) return <div className="py-16 text-center text-sm text-[#84675B]">Loading…</div>;
  const c = d.counts;
  const total = c.tasks_open + c.tasks_done + c.consents_pending + c.consents_signed;
  const done = c.tasks_done + c.consents_signed;
  const pct = total ? Math.round((done / total) * 100) : 0;
  const pendingTasks = d.tasks.filter((t) => t.status !== "completed");
  const doneTasks = d.tasks.filter((t) => t.status === "completed");
  const pendingConsents = d.consents.filter((x) => !x.signed);
  const signedConsents = d.consents.filter((x) => x.signed);

  return (
    <div className="space-y-5" data-testid="portal-onboarding-page">
      <div>
        <h1 className="font-heading text-2xl font-bold text-[#3E2723]">Your Onboarding</h1>
        <p className="text-sm text-[#7B6157]">Here is everything your care team has set up for you, and what is still pending.</p>
      </div>

      <div className="rounded-2xl bg-[#4E342E] p-5 text-white" data-testid="portal-onboarding-progress">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <div className="text-[11px] font-bold uppercase text-white/60">Onboarding Progress</div>
            <div className="font-heading text-xl font-bold">{done} of {total} completed</div>
          </div>
          <div className="text-right">
            <div className="font-heading text-3xl font-black tabular-nums">{pct}%</div>
          </div>
        </div>
        <div className="h-2 w-full overflow-hidden rounded-full bg-white/10">
          <div className="h-full bg-[#FFB300] transition-all" style={{ width: `${pct}%` }} />
        </div>
      </div>

      {(d.reminders || []).map((n) => (
        <div key={n.id} data-testid={`portal-reminder-${n.id}`}
          className="flex items-start gap-3 rounded-2xl border border-[#FFB300] bg-[#FFF3E0] p-4">
          <BellRing size={18} className="mt-0.5 shrink-0 text-[#8D5A00]" />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-bold text-[#8D6E00]">{n.title}</div>
            <div className="text-xs text-[#5D4037]">{n.body}</div>
          </div>
          {n.link && <Link to={n.link} className="shrink-0 rounded-full bg-[#4E342E] px-3.5 py-1.5 text-[11px] font-bold text-white">Open</Link>}
          <button data-testid={`portal-reminder-dismiss-${n.id}`} onClick={() => dismiss(n.id)}
            className="shrink-0 rounded-full p-1 text-[#7B6157] hover:bg-white/40"><X size={14} /></button>
        </div>
      ))}

      {pendingConsents.length > 0 && (
        <Link to="/portal/documents" data-testid="portal-onboarding-consents-cta"
          className="flex flex-wrap items-center gap-3 rounded-2xl border border-[#FFB300] bg-[#FFF8E1] p-4 hover:bg-[#FFECB3] transition-colors">
          <Sparkles size={18} className="text-[#8D5A00]" />
          <div>
            <div className="text-sm font-bold text-[#8D6E00]">{pendingConsents.length} form{pendingConsents.length > 1 ? "s" : ""} waiting for your signature</div>
            <div className="text-xs text-[#84675B]">Head to Documents to review and sign.</div>
          </div>
          <span className="ml-auto rounded-full bg-[#4E342E] px-4 py-1.5 text-xs font-bold text-white">Open Documents</span>
        </Link>
      )}

      <section className="rounded-2xl border border-[#EFEBE0] bg-white p-5" data-testid="portal-onboarding-tasks">
        <div className="mb-3 flex items-center gap-2">
          <ClipboardCheck size={16} className="text-[#7B6157]" />
          <h2 className="font-heading text-base font-bold text-[#3E2723]">Onboarding To-Dos</h2>
          <span className="text-xs text-[#7B6157]">{pendingTasks.length} pending{c.tasks_overdue ? `, ${c.tasks_overdue} overdue` : ""}</span>
        </div>
        {d.tasks.length === 0 ? <p className="text-sm text-[#84675B]">Your care team has not assigned any onboarding tasks yet.</p> : (
          <div className="space-y-2">
            {pendingTasks.map((t) => (
              <div key={t.id} data-testid={`portal-onboarding-task-${t.id}`}
                className={`flex items-center gap-3 rounded-xl border px-4 py-2.5 text-sm ${
                  t.overdue ? "border-[#FFCDD2] bg-[#FFF5F5]" : "border-[#EFEBE0] bg-[#FDFCF8]"}`}>
                {t.overdue ? <AlertTriangle size={15} className="text-[#C62828]" /> : <Clock size={15} className="text-[#7B6157]" />}
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-[#3E2723]">{t.title}</div>
                  <div className="text-[11px] text-[#7B6157]">{t.due_date ? `Due ${dayjs(t.due_date).format("MMM D, YYYY")}` : "No due date"}</div>
                </div>
                {t.overdue && <span className="rounded-full bg-[#C62828] px-2 py-0.5 text-[10px] font-bold text-white">Overdue</span>}
              </div>
            ))}
            {doneTasks.length > 0 && (
              <>
                <div className="pt-3 text-[10px] font-bold uppercase text-[#2E7D32]">Completed ({doneTasks.length})</div>
                {doneTasks.slice(0, 6).map((t) => (
                  <div key={t.id} data-testid={`portal-onboarding-task-done-${t.id}`}
                    className="flex items-center gap-3 rounded-xl border border-[#C8E6C9] bg-[#F1F8E9] px-4 py-2.5 text-sm">
                    <CheckCircle2 size={15} className="text-[#2E7D32]" />
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-[#5D4037] line-through">{t.title}</div>
                      <div className="text-[11px] text-[#7B6157]">{t.completed_at ? `Completed ${dayjs(t.completed_at).format("MMM D, YYYY")}` : ""}</div>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        )}
      </section>

      <section className="rounded-2xl border border-[#EFEBE0] bg-white p-5" data-testid="portal-onboarding-consents">
        <div className="mb-3 flex items-center gap-2">
          <FileSignature size={16} className="text-[#7B6157]" />
          <h2 className="font-heading text-base font-bold text-[#3E2723]">Consent Forms</h2>
          <span className="text-xs text-[#7B6157]">{signedConsents.length} signed of {d.consents.length}</span>
        </div>
        {d.consents.length === 0 ? <p className="text-sm text-[#84675B]">No consent forms have been sent to you yet.</p> : (
          <div className="space-y-2">
            {pendingConsents.map((c2) => (
              <div key={c2.id} data-testid={`portal-onboarding-consent-${c2.id}`}
                className={`flex items-center gap-3 rounded-xl border px-4 py-2.5 text-sm ${
                  c2.reminder_stale ? "border-[#FFB300] bg-[#FFF3E0]" : "border-[#FFECB3] bg-[#FFF8E1]"}`}>
                <FileSignature size={15} className="text-[#8D5A00]" />
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-[#3E2723]">{c2.template_name}</div>
                  <div className="text-[11px] text-[#7B6157]">
                    Sent {dayjs(c2.created_at).format("MMM D, YYYY")}
                    {c2.days_pending >= 3 && <span className="ml-2 rounded bg-[#B26A00] px-1.5 py-0.5 text-[10px] font-bold text-white">
                      {c2.days_pending} day{c2.days_pending === 1 ? "" : "s"} pending
                    </span>}
                  </div>
                </div>
                <Link to="/portal/documents" className="rounded-full bg-[#4E342E] px-3.5 py-1.5 text-[11px] font-bold text-white hover:bg-[#3E2723]">Review & sign</Link>
              </div>
            ))}
            {signedConsents.length > 0 && (
              <>
                <div className="pt-3 text-[10px] font-bold uppercase text-[#2E7D32]">Signed ({signedConsents.length})</div>
                {signedConsents.map((c2) => (
                  <div key={c2.id} data-testid={`portal-onboarding-consent-done-${c2.id}`}
                    className="flex items-center gap-3 rounded-xl border border-[#C8E6C9] bg-[#F1F8E9] px-4 py-2.5 text-sm">
                    <CheckCircle2 size={15} className="text-[#2E7D32]" />
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-[#5D4037]">{c2.template_name}</div>
                      <div className="text-[11px] text-[#7B6157]">
                        {c2.signed_at ? `Signed ${dayjs(c2.signed_at).format("MMM D, YYYY")}` : `Signed ${dayjs(c2.created_at).format("MMM D, YYYY")}`}
                        {c2.signed_via === "client_portal" && " (via portal)"}
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
